/**
 * search-profile-draft — the W2 Suchprofil review's LOCAL, per-user draft of the derived
 * proposal.
 *
 * `POST /workshops/search-profile/derive` is a METERED AI seam that runs ~10–30s, and its
 * proposal is transient by design: it lives in one client signal until the person accepts it
 * into their config. So any end of page-life — a reload, or the data-plane 401 route-to-login
 * round-trip (`services.ts`) — destroyed an artifact the person had already PAID for, and the
 * only way back was to derive again and pay again. That is the same money-fairness failure the
 * Werte-Sektion's resume-by-read fixed, in a flow with no captured-run recovery to fall back
 * on, so the client keeps the copy.
 *
 * The proposal is read-only under review (accept, or refine — which re-derives and replaces it
 * wholesale), so there are no edits to lose; what the draft protects is the deliverable itself.
 *
 * The transport shape lives here rather than in the surface because the guard is what makes
 * the localStorage boundary safe, and a shape without its guard invites a cast. The fields are
 * PRIMITIVES — a proposal is confirmed into a `SearchConfig` on accept and never persisted as
 * its own aggregate; the canonical shape is `@career/contract-v2`'s `SearchProfileDeriveOutput`,
 * which app-elements does not depend on (it consumes only `@career/definition-v2`).
 */

import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** One proposed role: the search term and the model's reason for it. */
export interface DerivedRole {
  term: string
  rationale: string
}

/** The derive seam's proposal, under HITL review. */
export interface DerivedProfile {
  roles: DerivedRole[]
  seniority: string[]
  locations: string[]
  excludeKeywords: string[]
  summary: string
}

/** The persisted review: the proposal plus its freshness clock. */
export interface SearchProfileReviewDraft {
  proposal: DerivedProfile
  /** When this draft was written (ISO-8601). */
  updatedAt: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string")

/** One role. Both fields may be empty — a model that returned a bare term is still reviewable. */
function isDerivedRole(v: unknown): v is DerivedRole {
  return isRecord(v) && typeof v["term"] === "string" && typeof v["rationale"] === "string"
}

function isDerivedProfile(v: unknown): v is DerivedProfile {
  if (!isRecord(v)) return false
  return (
    Array.isArray(v["roles"]) &&
    v["roles"].every(isDerivedRole) &&
    isStringArray(v["seniority"]) &&
    isStringArray(v["locations"]) &&
    isStringArray(v["excludeKeywords"]) &&
    typeof v["summary"] === "string"
  )
}

/**
 * A total, dependency-free guard for a persisted review. Structural only: it ensures the shape
 * is sound enough to render the review and to build the `SearchConfig` that accept saves — the
 * save endpoint re-validates the config server-side.
 */
export function isSearchProfileReviewDraft(v: unknown): v is SearchProfileReviewDraft {
  return isRecord(v) && isDerivedProfile(v["proposal"]) && typeof v["updatedAt"] === "string"
}

export type SearchProfileReviewDraftStore = LocalDraftStore<SearchProfileReviewDraft>

const KEY_PREFIX = "career:search-profile:review-draft:v1:"

/** A per-user review-draft store, guarded by {@link isSearchProfileReviewDraft}. */
export function createSearchProfileReviewDraftStore(userId: string | null, explicitStorage?: Storage): SearchProfileReviewDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isSearchProfileReviewDraft, explicitStorage)
}
